import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import * as Yup from "yup";
import { yearRange } from "../validation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import type {
  Form2848Representative,
  FormSubmission,
  GenerateFor,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox } from "./formik-controls";
import { PoaRepresentativesTable } from "./poa-representatives-table";

// --- Form M-2848 (MA Power of Attorney) editable payload --------------------
// snake_case keys mirror `apps/forms/mappings/form_m2848.py` `build_defaults`
// exactly (the `data` blob is passed through verbatim — no snake→camel).

/** MA matter row: two columns only (tax type + filing periods). */
export interface FormM2848Matter {
  tax_type: string;
  tax_years: string;
}

export interface FormM2848Data {
  generate_for: GenerateFor;
  representatives: Form2848Representative[];
  matters: FormM2848Matter[];
  /** The single rep's practitioner id the DOR sends notices/copies to. */
  all_notices_to: string;
  allow_comms: boolean;
  limitations: string;
  exceptions: string;
}

/** The M-2848 template's tax-matters table has three body rows. */
const MAX_MATTERS = 3;
const LIMITATIONS_MAX = 80;
const EXCEPTIONS_MAX = 80;

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const EMPTY_MATTER: FormM2848Matter = { tax_type: "", tax_years: "" };

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  all_notices_to: Yup.string().required("Choose who receives notices"),
  matters: Yup.array()
    .of(
      Yup.object({
        tax_type: Yup.string().max(80, "Too long"),
        tax_years: yearRange,
      }),
    )
    .max(MAX_MATTERS),
  limitations: Yup.string().max(LIMITATIONS_MAX, `Max ${LIMITATIONS_MAX} characters`),
  exceptions: Yup.string().max(EXCEPTIONS_MAX, `Max ${EXCEPTIONS_MAX} characters`),
});

function toInitialValues(data: Record<string, unknown>): FormM2848Data {
  const d = data as Partial<FormM2848Data>;
  const reps = d.representatives ?? [];
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: reps,
    matters:
      d.matters && d.matters.length > 0 ? d.matters : [{ ...EMPTY_MATTER }],
    all_notices_to: d.all_notices_to ?? (reps[0]?.practitioner ?? ""),
    allow_comms: d.allow_comms ?? false,
    limitations: d.limitations ?? "",
    exceptions: d.exceptions ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * "Tax Matters" — the MA M-2848's two-column table (Tax type + Filing
 * period(s)), capped at the template's three body rows.
 */
function MattersTable() {
  const { values } = useFormikContext<FormM2848Data>();
  const matters = values.matters;
  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax type</TableHead>
                  <TableHead className="w-56">Filing period(s)</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Personal Income Tax"
                        aria-label={`Tax type for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Filing periods for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form M-2848 fits up to {MAX_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}

/** Which single active rep the DOR sends notices/copies to. */
function NoticesField() {
  const { values } = useFormikContext<FormM2848Data>();
  const [field, , helpers] = useField<string>("all_notices_to");
  const activeReps = values.representatives.filter((r) => r.active);
  if (activeReps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        Select at least one active representative above to route notices.
      </p>
    );
  }
  return (
    <RadioGroup
      className="flex flex-col gap-2"
      aria-label="Send notices and communications to"
      value={field.value || ""}
      onValueChange={(v) => helpers.setValue(v)}
    >
      {activeReps.map((rep) => (
        <Label
          key={rep.practitioner}
          htmlFor={`notices-${rep.practitioner}`}
          className="flex items-center gap-2 font-normal"
        >
          <RadioGroupItem id={`notices-${rep.practitioner}`} value={rep.practitioner} />
          {rep.name}
        </Label>
      ))}
    </RadioGroup>
  );
}

function LimitedTextField({
  name,
  label,
  max,
}: {
  name: keyof FormM2848Data;
  label: string;
  max: number;
}) {
  const [field] = useField<string>(name);
  return (
    <div className="grid gap-2">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} maxLength={max} {...field} value={field.value ?? ""} />
      <span className="text-muted-foreground text-xs">Max {max} characters.</span>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Massachusetts Form M-2848 (Power of Attorney and Declaration of
 * Representative) — prefilled from the client + its practitioners. MA layout:
 * up to three reps, a two-column tax-matters table, notices routed to one rep,
 * an allow-communications oval, and additions/deletions + exceptions free-text.
 */
export function FormM2848Editor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <PoaRepresentativesTable />
        </section>

        <section className="flex flex-col gap-4">
          <SectionHeading>Tax Matters</SectionHeading>
          <MattersTable />
        </section>

        <Card className="gap-4 p-6">
          <SectionHeading>Notices & Communications</SectionHeading>
          <div className="grid gap-2">
            <span className="text-foreground text-sm font-medium">
              Send copies of notices and communications to
            </span>
            <NoticesField />
          </div>
          <FormikCheckbox
            name="allow_comms"
            label="Allow a DOR representative to communicate with any individual from the firm(s) listed above"
          />
        </Card>

        <Card className="gap-4 p-6">
          <SectionHeading>Additions, Deletions & Exceptions</SectionHeading>
          <LimitedTextField
            name="limitations"
            label="Specific additions or deletions to the acts otherwise authorized"
            max={LIMITATIONS_MAX}
          />
          <LimitedTextField
            name="exceptions"
            label="Exceptions to this power of attorney (specify to whom granted, date, and address)"
            max={EXCEPTIONS_MAX}
          />
        </Card>

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
